import { useQuery } from "@apollo/client";
import { useAuth } from "providers/AuthProvider";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo } from "react";
import { gql } from "types";

export type Country = string | undefined;

export type DeploymentCountryContextType = {
  country?: Country;
  isDeployedIn: (country: Country) => boolean;
};

export const DeploymentCountryContext =
  createContext<DeploymentCountryContextType>({
    isDeployedIn: () => false,
  });

const GET_COUNTRY = gql(/* GraphQL */ `
  query GetCountry {
    country
  }
`);

type Props = {
  children: ReactNode;
};

export const DeploymentCountryProvider = ({ children }: Props) => {
  const { authState } = useAuth();
  const { data } = useQuery(GET_COUNTRY, {
    skip: !authState?.accessToken,
  });
  const country = data?.country as Country;

  const isDeployedIn = useCallback(
    (c: Country) => {
      return country === c;
    },
    [country],
  );

  const contextValue = useMemo<DeploymentCountryContextType>(() => {
    return { country, isDeployedIn };
  }, [country, isDeployedIn]);

  return (
    <DeploymentCountryContext.Provider value={contextValue}>
      {children}
    </DeploymentCountryContext.Provider>
  );
};

export const useDeploymentCountry = (): DeploymentCountryContextType => {
  const context = useContext(DeploymentCountryContext);
  if (!context) {
    throw new Error(
      "useDeploymentCountry must be used within a DeploymentCountryProvider",
    );
  }
  return context;
};
